<template>
  <v-dialog
    v-model="openSelection"
    class="bg-white dark-bg-custom"
    :max-width="dialogMaxWidth"
    persistent
  >
    <v-card class="bg-white dark-bg-custom" v-if="!loading">
      <div class="flex justify-between items-center border-b px-5 py-4">
        <h3 class="text-xl font-medium m-0">Export Time Series Report</h3>
        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="flex h-[500px]">
        <!-- Left Panel: Available Columns -->
        <div class="w-1/3 border-r flex flex-col">
          <!-- <div class="p-3 border-b bg-gray-50 flex justify-between items-center">
            <span class="font-medium">Available Columns</span>
          </div> -->
          <div class="overflow-y-auto flex-1 p-3">
            <div class="mb-4 border-b flex justify-between items-center">
              <span class="font-medium">Available Columns</span>
            </div>
            <div v-for="(category, catIndex) in availableCategories" :key="catIndex" class="mb-4">
              <div class="font-semibold text-gray-700 mb-2 uppercase text-xs tracking-wider">
                {{ category.name }}
              </div>
              <div v-for="(col, colIndex) in category.columns" :key="colIndex" class="ml-2">
                <v-checkbox
                  v-model="selectedExportHeaderKeys"
                  :label="col"
                  :value="col"
                  dense
                  hide-details
                  class="my-1 text-sm custom-checkbox"
                ></v-checkbox>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel: Selected Columns -->
        <div class="w-2/3 flex flex-col">
          <div class="overflow-y-auto flex-1 p-3 bg-gray-50/50">
            <div class="mb-4 border-b flex justify-between items-center">
              <span class="font-medium"
                >Selected Columns ({{ selectedExportHeaderKeys.length }})</span
              >
              <v-btn
                text
                small
                color="primary"
                @click="selectedExportHeaderKeys = []"
                v-if="selectedExportHeaderKeys.length"
              >
                Clear All
              </v-btn>
            </div>
            <v-chip-group column>
              <v-chip
                v-for="col in selectedExportHeaderKeys"
                :key="col"
                close
                small
                color="blue lighten-5"
                text-color="blue darken-3"
                @click:close="removeColumn(col)"
                class="ma-1 font-medium"
              >
                {{ col }}
              </v-chip>
            </v-chip-group>
            <div
              v-if="!selectedExportHeaderKeys.length"
              class="flex flex-col items-center justify-center h-full text-gray-400"
            >
              <v-icon color="grey lighten-1" size="48">mdi-table-column-remove</v-icon>
              <span class="mt-2">No columns selected</span>
            </div>
          </div>
        </div>
      </div>

      <div class="px-5 py-4 border-t flex justify-between items-center bg-gray-50">
        <div>
          <p class="text-xs text-gray-500 mb-0">
            Note: This may take a while as it fetches granular data for all selected users.
          </p>
        </div>
        <div class="flex justify-end gap-3">
          <v-btn outlined @click="cancel" class="px-6" small> Cancel </v-btn>
          <v-btn
            :color="$vuetify.theme.currentTheme.primary"
            class="white--text px-6"
            @click="startExport"
            :disabled="!selectedExportHeaderKeys.length"
            small
          >
            Download Report
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-container class="bg-white dark-bg-custom" style="height: 300px" v-else>
      <v-row class="fill-height flex-col" align-content="center" justify="center">
        <v-col class="text-h6 text-center" cols="12"> Preparing Data... </v-col>
        <v-col class="text-subtitle-2 text-center text-gray-500 mb-4" cols="12">
          Processing {{ processedCount }} / {{ totalCount }} employees
        </v-col>
        <v-col cols="8" md="6">
          <v-progress-linear
            color="primary"
            :value="progressPercentage"
            rounded
            height="10"
            striped
          ></v-progress-linear>
        </v-col>
      </v-row>
    </v-container>
  </v-dialog>
</template>

<script>
import ExcelJS from 'exceljs'
import { sortDropdownOptions } from '~/utils/common'

export default {
  name: 'ExportTimeSeriesToExcel',
  props: {
    teamData: {
      type: Array,
      default: () => []
    },
    startDate: String,
    endDate: String,
    selectedColumns: {
      type: Array,
      default: () => []
    },
    attendanceSettings: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      openSelection: false,
      loading: false,
      dialogMaxWidth: '1000px',
      selectedExportHeaderKeys: [],
      processedCount: 0,
      totalCount: 0,
      currentUserProcessing: '',
      formattedData: [],
      maxManagers: 0,
      reporteeChain: this.$storage.getUniversal('userReporteeChain') || {},

      // Categorized Map for UI
      availableCategories: [
        {
          name: 'Employee Details',
          columns: [
            'Sr.No',
            'Employee Name',
            'Emp Id',
            'Department',
            'Designation',
            'Emp Address',
            'Emp Email',
            'Emp Mobile',
            'Direct Manager',
            'Manager Hierarchy'
          ]
        },
        {
          name: 'Attendance Details',
          columns: [
            'Date',
            'Attendance',
            'First Clock-In',
            'Last Clock-Out',
            // 'Distance (km)', // Moved to Visit / Activity Details
            'Shift Start Time',
            'Planned Working Hour',
            'Completed Working Hour',
            'Breaks Count',
            // 'Status', // Removed as per request
            'Overtime', // Added
            'Delay In Shift', // Added
            'Clock-In Image',
            'Clock-Out Image'
          ]
        },
        {
          name: 'Activity Details',
          columns: [
            'Activity Date',
            'Activity',
            'Activity Start Time',
            'Activity End Time',
            'Duration of Activity',
            'Activity Start Location',
            'Activity End Location'
          ]
        },
        {
          name: 'Visit Details',
          columns: [
            'Visit Date',
            'Customer Name',
            'Arrival Time',
            'Departure Time',
            'Duration of Visit',
            'Visit Location',
            'Daily Total Visits',
            'Distance Breakup Per Day', // Added by request
            'Total Distance Per Day',
            'Mode of Transportation',
            'Notes/Comments'
          ]
        },
        {
          name: 'Summary',
          columns: ['Month', 'Total Distance Traveled', 'Total Visits Done']
        }
      ],
      // Legacy support for options array as requested by user
      options: [
        'Visit Location',
        'Activity',
        'Activity Start Location',
        'Activity End Location',
        'Distance Traveled'
      ],
      excelStructure: null
    }
  },
  computed: {
    progressPercentage() {
      if (this.totalCount === 0) return 0
      return (this.processedCount / this.totalCount) * 100
    },
    allDates() {
      const dates = []
      // Ensure we have dayjs available
      if (!this.startDate || !this.endDate) return dates
      let curr = this.$dayjs(this.startDate)
      const end = this.$dayjs(this.endDate)
      while (curr.isBefore(end) || curr.isSame(end, 'day')) {
        dates.push(curr.format('YYYY-MM-DD'))
        curr = curr.add(1, 'day')
      }
      return dates
    }
  },
  methods: {
    removeColumn(col) {
      this.selectedExportHeaderKeys = this.selectedExportHeaderKeys.filter((c) => c !== col)
    },
    openDialog() {
      this.openSelection = true
      this.loading = false
      this.processedCount = 0
      this.formattedData = []

      // select all columns by default
      if (!this.selectedExportHeaderKeys.length) {
        const allCols = []
        this.availableCategories.forEach((cat) => {
          allCols.push(...cat.columns)
        })
        this.selectedExportHeaderKeys = [...allCols]
      }
    },
    cancel() {
      this.$emit('cancel')
      this.openSelection = false
      this.loading = false
    },
    async startExport() {
      this.loading = true
      this.totalCount = this.teamData.length
      this.processedCount = 0
      this.formattedData = []
      this.excelStructure = null

      try {
        await this.processData()
        this.exportFormattedExcel()
      } catch (error) {
        console.error('Export failed', error)
        if (this.$toast) this.$toast.error('Export failed: ' + error.message)
      } finally {
        this.cancel()
      }
    },
    async processData() {
      const rawUsers = this.teamData.map((d) => d.userId).filter((u) => u)
      // Deduplicate users
      const uniqueUsersMap = new Map()
      rawUsers.forEach((u) => {
        if (u._id && !uniqueUsersMap.has(u._id)) {
          uniqueUsersMap.set(u._id, u)
        }
      })
      const uniqueUsers = Array.from(uniqueUsersMap.values())

      const sortedEmployees = sortDropdownOptions(uniqueUsers, 'firstName')

      // Calculate Max Managers first
      this.maxManagers = 0
      sortedEmployees.forEach((employee) => {
        const managerChain = (this.reporteeChain[employee.orgEmployeeId] || this.reporteeChain[employee.employeeId] || this.reporteeChain[employee._id])?.managerChain || ''
        const managerList = managerChain ? managerChain.split(' -> ') : []
        this.maxManagers = Math.max(this.maxManagers, managerList.length)
      })

      // Build attendance map from teamData: userId -> Date(YYYY-MM-DD) -> attendanceStatus
      const attendanceMap = {}
      if (this.teamData && this.teamData.length) {
        this.teamData.forEach((record) => {
          const uId = record.userId?._id
          const dateStr =
            record.attendanceDateUTC || record.attendanceDate
              ? this.$dayjs(record.attendanceDateUTC || record.attendanceDate).format('YYYY-MM-DD')
              : null
          const status = record.attendanceStatus

          if (uId && dateStr && status) {
            if (!attendanceMap[uId]) {
              attendanceMap[uId] = {}
            }
            attendanceMap[uId][dateStr] = status
          }
        })
      }

      // Generate Structure BEFORE generating data rows
      this.excelStructure = this.getExportStructure()

      let serialNumber = 1

      for (const employee of sortedEmployees) {
        const employeeData = await this.fetchEmployeeTimeSeries(employee._id)
        const userAttendance = attendanceMap[employee._id] || {}
        this.generateRowsForEmployee(employee, employeeData, serialNumber, userAttendance)
        serialNumber++
        this.processedCount++
      }
    },
    async fetchEmployeeTimeSeries(userId) {
      const results = {}
      try {
        const response = await this.$axios.get(`/location-time-series/${userId}`, {
          params: {
            start: this.startDate,
            end: this.endDate,
            limit: 100000 // Ensure we get all data for the range
          }
        })

        const allPoints = response.data?.data?.data || []

        // Bucket points by date
        allPoints.forEach((point) => {
          const date = this.$dayjs(point.timestamp || point.createdAt).format('YYYY-MM-DD')
          if (!results[date]) {
            results[date] = []
          }
          results[date].push(point)
        })
      } catch (e) {
        console.error(`Failed to fetch time series for user ${userId}`, e)
      }
      return results
    },
    getExportStructure() {
      const structure = {
        mainHeaders: [],
        subHeaders: [],
        keys: []
      }

      const addSection = (title, potentialKeys) => {
        const matched = []

        // Handle Manager Details Separation
        if (title === 'Manager Details') {
          if (this.selectedExportHeaderKeys.includes('Direct Manager')) {
            matched.push('Direct Manager')
          }
          if (this.selectedExportHeaderKeys.includes('Manager Hierarchy')) {
            for (let i = 1; i <= this.maxManagers; i++) {
              matched.push(`Manager Level ${i}`)
            }
            matched.push('Manager Hierarchy')
          }
        } else {
          potentialKeys.forEach((k) => {
            // special check for Direct Manager if it's in Employee Details list in UI but belongs to Manager Details in Export
            if (title === 'Employee Details' && k === 'Direct Manager') return
            if (this.selectedExportHeaderKeys.includes(k)) matched.push(k)
          })
        }

        if (matched.length > 0) {
          const start = structure.subHeaders.length + 1
          structure.subHeaders.push(...matched)
          structure.keys.push(...matched)
          structure.mainHeaders.push({
            title: title,
            start: start,
            end: start + matched.length - 1
          })
        }
      }

      addSection('Employee Details', [
        'Sr.No',
        'Employee Name',
        'Emp Id',
        'Department',
        'Designation',
        'Emp Address',
        'Emp Email',
        'Emp Mobile'
      ])
      addSection('Manager Details', [])
      addSection('Daily Attendance Reports', [
        'Date',
        'Attendance',
        'First Clock-In',
        'Last Clock-Out',
        // 'Distance (km)', // Moved
        'Shift Start Time',
        'Planned Working Hour',
        'Completed Working Hour',
        'Breaks Count',
        // 'Status',
        'Overtime',
        'Delay In Shift',
        'Clock-In Image',
        'Clock-Out Image'
      ])

      addSection('Activity Details', [
        'Activity Date',
        'Activity',
        'Activity Start Time',
        'Activity End Time',
        'Duration of Activity',
        'Activity Start Location',
        'Activity End Location'
      ])

      addSection('Visit Details', [
        'Visit Date',
        'Customer Name',
        'Arrival Time',
        'Departure Time',
        'Duration of Visit',
        'Visit Location',
        'Daily Total Visits',
        'Distance Breakup Per Day',
        'Total Distance Per Day',
        'Mode of Transportation',
        'Notes/Comments'
      ])
      addSection('Monthly Travel Summary', [
        'Month',
        'Total Distance Traveled',
        'Total Visits Done'
      ])

      return structure
    },
    generateRowsForEmployee(employee, timeSeriesData, serialNumber, userAttendance) {
      const managerChain = (this.reporteeChain[employee.orgEmployeeId] || this.reporteeChain[employee.employeeId] || this.reporteeChain[employee._id])?.managerChain || ''
      const managerList = managerChain ? managerChain.split(' -> ') : []
      const managerColumns = Array.from(
        { length: this.maxManagers },
        (_, i) => managerList[i] || '-'
      )
      const name = employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`

      const directManager =
        employee.reportesTo?.fullName ||
        (employee.reportesTo
          ? `${employee.reportesTo.firstName || ''} ${employee.reportesTo.lastName || ''}`
          : '-')

      let totalDistance = 0
      let totalUniqueVisits = 0 // For Summary
      let showTotalDistance = this.selectedExportHeaderKeys.includes('Total Distance Traveled')
      Object.keys(timeSeriesData).forEach((date) => {
        const points = timeSeriesData[date]
        if (points && points.length > 0) {
          const dist = this.calculateTotalDistance(points)
          totalDistance += dist

          // Calculate unique visits for the day and add to total
          const dailyUnique = new Set()
          points.forEach((p) => {
            const task = p.activity?.caseTaskDetails
            if (task) {
              dailyUnique.add(task._id || task)
            }
          })
          totalUniqueVisits += dailyUnique.size
        }
      })

      this.allDates.forEach((date, index) => {
        const points = timeSeriesData[date] || []
        const formattedDate = this.$dayjs(date).format('DD/MM/YYYY')
        let isFirstPairForDate = true

        const statusFromApi = userAttendance ? userAttendance[date] : null
        const rowData = this.processDailyPoints(points, statusFromApi)

        const getVal = (key) => {
          // Check dynamic manager level key
          if (key.startsWith('Manager Level ')) {
            const levelStr = key.replace('Manager Level ', '')
            const levelIndex = parseInt(levelStr) - 1
            return index === 0 ? managerList[levelIndex] || '-' : ''
          }
          const shiftStartTime =
            this.$dayjs(this.attendanceSettings?.shiftStartTime, 'HH:mm').format('hh:mm A') || '-'
          switch (key) {
            // Employee
            case 'Sr.No':
              return index === 0 ? serialNumber : ''
            case 'Employee Name':
              return index === 0 ? name : ''
            case 'Emp Id':
              return index === 0 ? employee.orgEmployeeId || employee.employeeId : ''
            case 'Department':
              return index === 0 ? employee.department?.departmentName || '-' : ''
            case 'Designation':
              return index === 0 ? employee.designation?.designationName || '-' : ''
            case 'Emp Address':
              return index === 0 ? employee.address || '' : ''
            case 'Emp Email':
              return index === 0 ? employee.email : ''
            case 'Emp Mobile':
              return index === 0 ? employee.mobile : ''

            // Manager - Direct & Hierarchy
            case 'Direct Manager':
              return index === 0 ? directManager : ''
            case 'Manager Hierarchy':
              return index === 0 ? managerChain : ''

            // Attendance
            case 'Date':
              return formattedDate
            case 'Attendance':
              return rowData.attendanceStatus
            case 'First Clock-In':
              return rowData.firstClockIn
            case 'Last Clock-Out':
              return rowData.lastClockOut
            case 'Total Distance Per Day':
              return rowData.dailyDistance ? rowData.dailyDistance.toFixed(2) : '0'
            case 'Shift Start Time': {
              const val = this.attendanceSettings?.shiftStartTime
              return val ? `${shiftStartTime}` : '-'
            }
            case 'Planned Working Hour': {
              const val = this.attendanceSettings?.workingHours
              return val ? `${val} hrs` : '-'
            }
            case 'Completed Working Hour':
              return rowData.workingHours || '-'
            case 'Breaks Count':
              return rowData.breaksCount || '0'
            case 'Status':
              return rowData.status || '-'
            case 'Overtime':
              return '-' // Placeholder: logic requires Shift/Attendance Record
            case 'Delay In Shift':
              return '-' // Placeholder: logic requires Shift/Attendance Record
            case 'Clock-In Image':
              return '-'
            case 'Clock-Out Image':
              return '-'

            case 'Daily Total Visits':
              return rowData.dailyVisitCount || '0'

            // Summary
            case 'Month':
              return index === 0 ? this.$dayjs(date).format('MMMM YYYY') : ''
            case 'Total Distance Traveled':
              return index === 0 && showTotalDistance ? totalDistance.toFixed(2) : ''
            case 'Total Visits Done':
              return index === 0 ? totalUniqueVisits : ''

            default:
              return ''
          }
        }

        const constructRow = (pair = null, isFirst = true) => {
          const row = []

          this.excelStructure.keys.forEach((col) => {
            if (this.isPairColumn(col)) {
              if (!pair) {
                row.push(isFirst ? '-' : '-')
              } else {
                row.push(this.getPairValue(pair, col))
              }
            } else {
              // Standard column
              if (!isFirst && this.isEmployeeColumn(col)) {
                row.push('')
              } else if (!isFirst && this.isAttendanceColumn(col)) {
                if (col === 'Daily Total Visits' || col === 'Total Distance Per Day') {
                  row.push('')
                } else {
                  row.push(isFirst ? getVal(col) : '-')
                }
              } else if (!isFirst && this.isSummaryColumn(col)) {
                row.push('')
              } else if (col.startsWith('Manager Level') && !isFirst) {
                row.push('')
              } else {
                row.push(getVal(col))
              }
            }
          })
          return row
        }

        if (rowData.pairs && rowData.pairs.length > 0) {
          rowData.pairs.forEach((pair, i) => {
            const row = constructRow(pair, isFirstPairForDate)
            this.formattedData.push(row)
            isFirstPairForDate = false
          })
        } else {
          const row = constructRow(null, true)
          this.formattedData.push(row)
        }
      })
    },
    isEmployeeColumn(col) {
      return [
        'Sr.No',
        'Employee Name',
        'Emp Id',
        'Department',
        'Designation',
        'Emp Address',
        'Emp Email',
        'Emp Mobile',
        'Direct Manager',
        'Manager Hierarchy'
      ].includes(col)
    },
    isAttendanceColumn(col) {
      return [
        'Date',
        'Attendance',
        'First Clock-In',
        'Last Clock-Out',
        'Total Distance Per Day',
        'Shift Start Time',
        'Planned Working Hour',
        'Completed Working Hour',
        'Breaks Count',
        'Status',
        'Overtime',
        'Delay In Shift',
        'Daily Total Visits',
        'Clock-In Image',
        'Clock-Out Image'
      ].includes(col)
    },
    isPairColumn(col) {
      return [
        'Customer Name',
        'Date (Visit/Activity Date)',
        'Activity',
        'Activity Start Location',
        'Activity End Location',
        'Activity Start Time',
        'Activity End Time',
        'Duration of Activity', // Added
        'Arrival Time',
        'Departure Time',
        'Duration of Visit', // Renamed
        'Visit Location',
        'Mode of Transportation',
        'Distance Breakup Per Day',
        'Notes/Comments'
      ].includes(col)
    },
    isSummaryColumn(col) {
      return ['Month', 'Total Distance Traveled', 'Total Visits Done'].includes(col)
    },
    getPairValue(pair, col) {
      const formattedDate = this.$dayjs(pair.originArrivalTime || new Date()).format('DD/MM/YYYY')
      const arrivalTime = pair.originArrivalTime
        ? this.$dayjs(pair.originArrivalTime).format('hh:mm A')
        : '-'
      const departureTime = pair.originDepartureTime
        ? this.$dayjs(pair.originDepartureTime).format('hh:mm A')
        : '-'

      switch (col) {
        case 'Customer Name':
          return pair.customerName || '-'
        case 'Date (Visit/Activity Date)':
          return formattedDate
        case 'Arrival Time':
          return arrivalTime
        case 'Departure Time':
          return departureTime
        case 'Duration of Visit': // Renamed
          return pair.originDuration || '-'
        case 'Visit Location':
          return pair.origin || '-'
        case 'Activity':
          return pair.originAction || '-'
        case 'Activity Start Location':
          return pair.origin || '-'
        case 'Activity End Location':
          return pair.destination || '-'
        case 'Activity Start Time':
          return arrivalTime
        case 'Activity End Time':
          return departureTime
        case 'Duration of Activity': // Added
          return pair.originDuration || '-'
        case 'Mode of Transportation':
          return ''
        case 'Distance Breakup Per Day':
          return pair.segmentDistance !== undefined && pair.segmentDistance !== null
            ? pair.segmentDistance.toFixed(2)
            : '-'
        case 'Notes/Comments':
          return ''
        default:
          return '-'
      }
    },
    processDailyPoints(points, statusFromApi) {
      if (!points || points.length === 0) {
        return {
          attendanceStatus: statusFromApi || '-',
          firstClockIn: '-',
          lastClockOut: '-',
          dailyDistance: 0,
          pairs: []
        }
      }

      points.sort(
        (a, b) => new Date(a.timestamp || a.createdAt) - new Date(b.timestamp || b.createdAt)
      )

      const firstPoint = points[0]
      const lastPoint = points.length > 1 ? points[points.length - 1] : firstPoint

      const firstTime = this.$dayjs(firstPoint.timestamp || firstPoint.createdAt)
      const lastTime = this.$dayjs(lastPoint.timestamp || lastPoint.createdAt)

      let totalDist = this.calculateTotalDistance(points)
      const stops = this.groupPointsIntoStops(points)
      const pairs = []

      if (stops.length > 0) {
        // Iterate ALL stops to list them as rows
        for (let i = 0; i < stops.length; i++) {
          const sCurr = stops[i]
          const sPrev = i > 0 ? stops[i - 1] : null
          const sNext = i < stops.length - 1 ? stops[i + 1] : null

          let distFromPrev = 0
          let distToNext = 0
          if (sPrev) {
            distFromPrev = this.calculateDistance(sPrev.lat, sPrev.lng, sCurr.lat, sCurr.lng)
          }
          if (sNext) {
            distToNext = this.calculateDistance(sCurr.lat, sCurr.lng, sNext.lat, sNext.lng)
          }

          const durationMs = this.$dayjs(sCurr.endTime).diff(this.$dayjs(sCurr.startTime))

          pairs.push({
            origin: sCurr.address,
            destination: sNext ? sNext.address : '-',
            originAction: sCurr.action,
            destinationAction: sNext ? sNext.action : '-',
            distance: distToNext, // For 'Distance (km)' / 'Distance Traveled' mapping to next
            segmentDistance: distFromPrev, // New 0-based column
            customerName: '-',
            originArrivalTime: sCurr.startTime,
            originDepartureTime: sCurr.endTime,
            originDuration: this.formatDuration(durationMs)
          })
        }
      } else {
        const distFirstLast = this.calculateDistance(
          firstPoint.lat || firstPoint.location?.coordinates[1] || 0,
          firstPoint.lng || firstPoint.location?.coordinates[0] || 0,
          lastPoint.lat || lastPoint.location?.coordinates[1] || 0,
          lastPoint.lng || lastPoint.location?.coordinates[0] || 0
        )

        if (distFirstLast > 0.1) {
          // If significant movement detected (>100m) but no 'STILL' stops grouped
          // Create 2 rows: Start and End
          const durationMs = lastTime.diff(firstTime)

          // Row 1: Start -> End
          pairs.push({
            origin: firstPoint.address || firstPoint.location?.address || '-',
            destination: lastPoint.address || lastPoint.location?.address || '-',
            originAction: '-',
            destinationAction: '-',
            distance: distFirstLast, // Distance to next
            segmentDistance: 0, // Start of day
            customerName: '-',
            originArrivalTime: firstTime,
            originDepartureTime: null,
            originDuration: '-'
          })

          // Row 2: End
          pairs.push({
            origin: lastPoint.address || lastPoint.location?.address || '-',
            destination: '-',
            originAction: '-',
            destinationAction: '-',
            distance: 0,
            segmentDistance: distFirstLast, // Distance from prev
            customerName: '-',
            originArrivalTime: lastTime,
            originDepartureTime: null,
            originDuration: '-'
          })
        } else {
          // Stationary / Single point
          pairs.push({
            origin: firstPoint.address || firstPoint.location?.address || '-',
            destination: lastPoint.address || lastPoint.location?.address || '-',
            originAction: '-',
            destinationAction: '-',
            distance: totalDist,
            segmentDistance: 0,
            customerName: '-',
            originArrivalTime: null,
            originDepartureTime: null,
            originDuration: '-'
          })
        }
      }

      const workingHours = lastTime.diff(firstTime, 'hour', true)

      return {
        attendanceStatus: statusFromApi || '-',
        firstClockIn: firstTime.format('hh:mm A'),
        lastClockOut: points.length > 1 ? lastTime.format('hh:mm A') : '-',
        dailyDistance: totalDist,
        workingHours: workingHours > 0 ? workingHours.toFixed(2) + ' hrs' : '-',
        dailyVisitCount: this.calculateDailyUniqueVisits(points),
        pairs: pairs
      }
    },
    calculateDailyUniqueVisits(points) {
      if (!points) return 0
      const unique = new Set()
      points.forEach((p) => {
        const task = p.activity?.caseTaskDetails
        if (task) {
          unique.add(task._id || task)
        }
      })
      return unique.size
    },
    groupPointsIntoStops(points) {
      const groups = []
      let currentGroup = null

      points.forEach((p) => {
        const state = p.state || 'UNKNOWN'
        const isStill = state === 'STILL'
        const pAddress = (p.address || p.location?.address || '').trim()
        const pPrefix = pAddress.split(',')[0].trim()
        const pTime = p.timestamp || p.createdAt

        let lat, lng
        if (p.location?.coordinates) {
          lng = p.location.coordinates[0]
          lat = p.location.coordinates[1]
        } else {
          lat = p.lat
          lng = p.lng
        }
        if (!lat || !lng) return

        if (isStill) {
          if (currentGroup && currentGroup.addressPrefix === pPrefix) {
            currentGroup.endTime = pTime
          } else {
            if (currentGroup) groups.push(currentGroup)
            currentGroup = {
              address: pAddress,
              addressPrefix: pPrefix,
              lat: lat,
              lng: lng,
              startTime: pTime,
              endTime: pTime,
              action: p.activity?.action || '-'
            }
          }
        } else {
          if (currentGroup) {
            groups.push(currentGroup)
            currentGroup = null
          }
        }
      })
      if (currentGroup) groups.push(currentGroup)
      return groups
    },
    calculateDistance(lat1, lon1, lat2, lon2) {
      if (!lat1 || !lon1 || !lat2 || !lon2) return 0
      const R = 6371
      const dLat = this.deg2rad(lat2 - lat1)
      const dLon = this.deg2rad(lon2 - lon1)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },
    deg2rad(deg) {
      return deg * (Math.PI / 180)
    },
    formatDuration(ms) {
      if (!ms || ms < 0) ms = 0
      const seconds = Math.floor(ms / 1000)
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60

      if (h > 0) return `${h}h ${m}m ${s}s`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },
    calculateTotalDistance(points) {
      let total = 0
      for (let i = 0; i < points.length - 1; i++) {
        let lat1, lng1, lat2, lng2
        if (points[i].location?.coordinates) {
          lng1 = points[i].location.coordinates[0]
          lat1 = points[i].location.coordinates[1]
        } else {
          lat1 = points[i].lat
          lng1 = points[i].lng
        }

        if (points[i + 1].location?.coordinates) {
          lng2 = points[i + 1].location.coordinates[0]
          lat2 = points[i + 1].location.coordinates[1]
        } else {
          lat2 = points[i + 1].lat
          lng2 = points[i + 1].lng
        }

        if (lat1 && lng1 && lat2 && lng2) total += this.calculateDistance(lat1, lng1, lat2, lng2)
      }
      return total
    },
    async exportFormattedExcel() {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Muster Report')

      const { mainHeaders, subHeaders } = this.excelStructure

      // Row 1: Main Headers
      const mainHeaderRow = worksheet.getRow(1)
      mainHeaders.forEach((mh, index) => {
        worksheet.mergeCells(1, mh.start, 1, mh.end)
        const cell = mainHeaderRow.getCell(mh.start)
        cell.value = mh.title
        cell.font = { size: 14, bold: true }
        cell.alignment = { horizontal: 'center', vertical: 'middle' }
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: index % 2 === 0 ? 'FFCFE2F3' : 'FFD9EAD3' }
        }
      })
      mainHeaderRow.height = 25

      // Row 2: Sub Headers
      const subHeaderRow = worksheet.getRow(2)
      subHeaderRow.values = subHeaders
      subHeaderRow.font = { bold: true, size: 12 }
      subHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' }

      // Data Rows
      this.formattedData.forEach((rowData) => {
        worksheet.addRow(rowData)
      })

      worksheet.columns.forEach((column) => {
        column.width = 22
      })

      const fileName = `Muster_Report_${this.startDate}_${this.endDate}.xlsx`
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
  mounted() {
    this.$root.$on('export-time-series-report', this.openDialog)
  },
  beforeDestroy() {
    this.$root.$off('export-time-series-report', this.openDialog)
  }
}
</script>

<style scoped>
.custom-checkbox {
  margin-top: 0 !important;
}
</style>
